import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import { CategoryNavItem, SeasonNavItem } from './components/nav-items';
import { CareerAndSeasonStatsTable } from './components/table';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import {
    careerAndSeasonStatsBenchmarkingSelector,
    careerAndSeasonStatsDataTypeSelector,
    careerAndSeasonStatsNavSelector
} from 'store/modules/statspro/career-and-season-stats/selectors';
import {
    BenchmarkingLegend,
    BenchmarkingExplainer
} from 'common/react/components/benchmarking';
import {
    BenchmarkingSwitch,
    dataTypeOptions,
    DataTypeToggle,
    Heading
} from './components/header';
import { useGetCompetitionsQuery } from 'common/store/modules/api/afl-api';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { useWindowSize } from 'common/react/hooks/use-window-resize';
import { getTranslation } from 'common/react/utils/translations';
import { initialiseNav } from 'common/store/modules/statspro/thunks/initialise-nav';
import { careerAndSeasonStatsActions } from 'common/store/modules/statspro/career-and-season-stats';
import { useHasChangedFromDefaults } from 'common/react/components/competition-nav/hooks/use-has-changed-from-defaults';
import { useResetToDefaults } from '../common/hooks/use-reset-to-defaults';

const NavWrapper = ({ children }) => (
    <div className="competition-nav competition-nav--no-margin-top filter-dropdown">
        {children}
    </div>
);

NavWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

export const CareerAndSeasonStats = ({
    title,
    playerProviderId,
    compId,
    showBenchmarkingAflw,
    hideClubLogos
}) => {
    const isAFLW = PULSE.app.common.url.checkBaseRoute('aflw');

    const { competition } = useGetCompetitionsQuery(
        {},
        {
            selectFromResult: ({ data }) => ({
                competition: data?.find((item) => item.id === parseInt(compId))
            })
        }
    );

    const windowSize = useWindowSize();
    const dispatch = useDispatch();
    const isResponsive = () =>
        windowSize.width < PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const benchmarking = useSelector(careerAndSeasonStatsBenchmarkingSelector);
    const nav = useSelector(careerAndSeasonStatsNavSelector);
    const dataType = useSelector(careerAndSeasonStatsDataTypeSelector);
    const options = useCompetitionNavigationOptions(nav);

    useEffect(() => {
        dispatch(
            initialiseNav({
                slice: 'career-and-season-stats',
                initialValues: {
                    competitionId: compId,
                    seasonId: -1
                }
            })
        );
    }, [dispatch]);

    // useCareerAndSeasonStatsNavSync(options, competition?.id);

    const showBenchmarkingAFLWBool = showBenchmarkingAflw === 'true';
    const showBenchmarking = !isAFLW || (isAFLW && showBenchmarkingAFLWBool);

    const isAFLM =
        competition?.providerId ===
        PULSE.app.common.CONSTANTS.PROVIDER_IDS.PREMIERSHIP;

    const renderAveragesBenchmarking =
        showBenchmarking &&
        (dataType.type === 'averages' ||
            dataType.type === 'benchmarkedAverages');

    const careerAndSeasonStatsRef = useRef();

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: {
            category: nav.category,
            season: nav.season,
            dataType,
            benchmarking
        },
        options: {
            ...options,
            dataTypes: dataTypeOptions
        },
        filtersFields: [
            'category',
            'career-season',
            'benchmarking',
            'dataType'
        ],
        filtersRef: careerAndSeasonStatsRef
    });

    const { hasChangedFromDefaults } = useHasChangedFromDefaults({
        state: { nav: nav },
        initialValues: {
            category: 'Key Stats',
            seasonId: -1
        }
    });

    const resetToDefaults = useResetToDefaults({
        slice: 'career-and-season-stats',
        initialValues: {
            competitionId: compId,
            seasonId: -1,
            initialParams: {
                category: 'Key Stats'
            }
        },
        actions: careerAndSeasonStatsActions
    });

    return (
        <div ref={careerAndSeasonStatsRef}>
            <StatsSection.Main NavWrapper={NavWrapper}>
                <StatsSection.Heading title={title} />
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    <StatsSection.NavItems>
                        <CategoryNavItem hideLabel />
                        <SeasonNavItem
                            isAFLW={isAFLW}
                            playerId={playerProviderId}
                            classes="career-and-season-season-nav-item"
                        />
                        {hasChangedFromDefaults && !showResponsiveItems ? (
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        ) : null}
                    </StatsSection.NavItems>
                    {hasChangedFromDefaults && showResponsiveItems ? (
                        <StatsSection.NavFilterItems hideReset={true}>
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        </StatsSection.NavFilterItems>
                    ) : null}
                </StatsSection.Nav>
                <StatsSection.Content>
                    <StatsSection.ContentHeader>
                        <StatsSection.ContentHeaderHeading>
                            <Heading />
                        </StatsSection.ContentHeaderHeading>
                        {nav.season.id === -1 && (
                            <StatsSection.ContentHeaderOptions>
                                {renderAveragesBenchmarking && (
                                    <BenchmarkingSwitch />
                                )}
                                <DataTypeToggle />
                            </StatsSection.ContentHeaderOptions>
                        )}
                        <StatsSection.ContentHeaderBelow>
                            {benchmarking?.isActive && <BenchmarkingLegend />}
                            {benchmarking?.isActive && (
                                <BenchmarkingExplainer />
                            )}
                        </StatsSection.ContentHeaderBelow>
                    </StatsSection.ContentHeader>
                    <StatsSection.ContentBody>
                        <CareerAndSeasonStatsTable
                            isAFLW={isAFLW}
                            isAFLM={isAFLM}
                            hideClubLogos={
                                hideClubLogos === 'true' ? true : false
                            }
                            playerId={playerProviderId}
                        />
                    </StatsSection.ContentBody>
                </StatsSection.Content>
            </StatsSection.Main>
        </div>
    );
};

CareerAndSeasonStats.propTypes = {
    title: PropTypes.string.isRequired,
    compseasonId: PropTypes.string.isRequired,
    playerProviderId: PropTypes.string.isRequired,
    showBenchmarkingAflw: PropTypes.string,
    hideClubLogos: PropTypes.string,
    compId: PropTypes.string
};
